// src/pages/Home.js
import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Link } from 'react-router-dom';
import { useMailer } from '../hooks/useMailer';
import { Helmet } from "react-helmet";
import '../style.css';

function Home() {
  const { send: sendMail, loading: mailLoading, submitted: mailSubmitted, ErrorBox } = useMailer('contact');
  const [data, setData] = useState([]);

  useEffect(() => {
    fetch("https://culturlingua.com/api.php")
      .then((response) => response.json())
      .then((data) => setData(data))
      .catch((error) => console.error("Error fetching data:", error));
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMail(Object.fromEntries(new FormData(e.target).entries()));
  };

  return (
    <div className="home-wrapper">
      <Helmet>
        <title>Culturlingua | Inglés en Tlaquepaque y Online — Guadalajara</title>
        <meta name="description" content="Aprende inglés con profesores certificados en Tlaquepaque o 100% online. Más de 20 años formando hablantes reales. Niveles A1 a C1, horarios flexibles." />
        <meta name="keywords" content="clases de inglés Tlaquepaque, inglés online Guadalajara, curso de inglés, escuela de idiomas Jalisco, inglés certificación" />
        <meta property="og:title" content="Culturlingua | Inglés en Tlaquepaque y Online" />
        <meta property="og:description" content="Más de 20 años formando hablantes reales. Clases presenciales en Tlaquepaque y online desde cualquier lugar." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://culturlingua.com/" />
        <meta property="og:image" content="https://culturlingua.com/assets/og-culturlingua.jpg" />
        <meta property="og:locale" content="es_MX" />
        <meta property="og:site_name" content="Culturlingua" />
        <link rel="canonical" href="https://culturlingua.com/" />


        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LanguageSchool",
            "name": "Culturlingua",
            "url": "https://culturlingua.com",
            "image": "https://culturlingua.com/assets/logo.png",
            "telephone": "+523333449139",
            "priceRange": "$$",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Reforma 31",
              "addressLocality": "Tlaquepaque",
              "addressRegion": "Jalisco",
              "postalCode": "45500",
              "addressCountry": "MX"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "20.6409",
              "longitude": "-103.2933"
            },
            "areaServed": [
              "Guadalajara",
              "Tlaquepaque",
              "Zapopan",
              "Jalisco",
              "Mexico"
            ],
            "sameAs": [
              "https://facebook.com/culturlinguagdl"
            ]
          })}
        </script>
      </Helmet>

      {/* ── HERO ── */}
      <section className="hero-modern">
        <div className="hero-bg"></div>
        <div className="hero-inner">

          {/* Left: copy */}
          <div className="hero-left">
            <div className="hero-eyebrow">Tlaquepaque · Guadalajara · Online</div>
            <h1 className="hero-title">
              Aprende inglés con<br />
              <em>confianza real</em><br />
              en grupos pequeños.
            </h1>
            <p className="hero-sub">
              Máximo 12 alumnos por grupo. Clases presenciales en Tlaquepaque
              o en vivo online — con profesores certificados que conocen tu progreso por nombre.
            </p>
            <div className="hero-buttons">
              <Link to="https://wa.me/5213333449139?text=Hola%2C%20quisiera%20agendar%20una%20clase%20de%20prueba%20gratis." className="btn-glow">🗓️ Agenda tu clase de prueba gratis</Link>
              <Link to="https://linguahub.culturlingua.com/gral/public/inscripcion.php?utm_source=sitio&utm_medium=cta&utm_campaign=home_hero" className="btn-outline">🎯 ¡Aparta tu lugar!</Link>
              <Link to="https://linguahub.culturlingua.com/gral/placement_test/examen.php?utm_source=sitio&utm_medium=cta&utm_campaign=home_hero" className="btn-outline">🏆 ¡Conoce tu nivel!</Link>
            </div>
            <div className="trust-badges">
            <span>✅ Máx. 12 alumnos por grupo</span>
            <span>✅ Profesores Certificados</span>
            <span>✅ Online y Presencial</span>
          </div>
          </div>

          {/* Right: stats cards */}
          <div className="hero-visual">
            <div className="stat-card">
              <div className="stat-number">20+</div>
              <div className="stat-label">Años formando hablantes de inglés en Jalisco</div>
            </div>
            <div className="level-card">
              <div className="level-card-title">Niveles que impartimos</div>
              <div className="level-bars">
                {[['A1','100%'],['B1','100%'],['B2','100%'],['C1','25%']].map(([lvl, w]) => (
                  <div className="level-bar" key={lvl}>
                    <span className="level-bar-label">{lvl}</span>
                    <div className="level-bar-track">
                      <div className="level-bar-fill" style={{width: w}}></div>
                    </div>
                    <span className="level-bar-pct">✓</span>
                  </div>
                ))}
              </div>
            </div>
            <Link to="/Examen-de-ubicacion" className="exam-cta-card">
              <div className="exam-icon">📝</div>
              <div className="exam-text">
                <strong>¿No sabes tu nivel?</strong>
                <span>Haz el examen de ubicación gratis →</span>
              </div>
            </Link>
          </div>

        </div>
      </section>

      {/* ── STATS BAND ── */}
      <div className="stats-band">
        <div className="stats-band-inner">
          {[
            ['20+', 'Años de experiencia'],
            ['300+', 'Alumnos activos'],
            ['Máx. 12', 'Alumnos por grupo'],
            ['100%', 'Profesores certificados'],
          ].map(([num, label]) => (
            <div className="stat-band-item" key={label}>
              <div className="stat-band-num">{num}</div>
              <div className="stat-band-label">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── MODALIDADES ── */}
      <section className="modalidades-section">
        <div className="modalidades-header">
          <div className="section-tag">Modalidades</div>
          <h2 className="section-headline">Tu aprendizaje,<br /><em>a tu manera</em></h2>
          <p className="section-sub">Elige la modalidad que mejor se adapte a tu ritmo de vida y metas.</p>
        </div>
        <div className="cards-grid">

          <div className="modal-card">
            <div className="modal-card-accent"></div>
            <div className="modal-card-body">
              <div className="modal-icon">🏫</div>
              <h3>Presencial</h3>
              <p>Inmersión total en nuestras aulas en Tlaquepaque, Jalisco.</p>
              <ul className="modal-features">
                <li>Grupos pequeños y personalizados</li>
                <li>Profesores certificados en aula</li>
                <li>Horarios matutinos y vespertinos</li>
              </ul>
              <Link to="/Cursos" className="modal-link">Ver cursos →</Link>
            </div>
          </div>

          <div className="modal-card">
            <div className="modal-card-accent green"></div>
            <div className="modal-card-body">
              <div className="modal-icon">💻</div>
              <h3>Online</h3>
              <p>Aprende desde cualquier lugar con plataforma digital interactiva.</p>
              <ul className="modal-features">
                <li>Clases en vivo con profesor real</li>
                <li>Plataforma LinguaSkills incluida</li>
                <li>Clubes de conversación internacionales</li>
              </ul>
              <Link to="/ingles-online" className="modal-link">Ver clases online →</Link>
            </div>
          </div>

          <div className="modal-card">
            <div className="modal-card-accent purple"></div>
            <div className="modal-card-body">
              <div className="modal-icon">🏢</div>
              <h3>Empresas</h3>
              <p>Programas corporativos con seguimiento y reportes para RRHH.</p>
              <ul className="modal-features">
                <li>Grupos in-company o remotos</li>
                <li>Reporte de avance por empleado</li>
                <li>Horarios adaptados al equipo</li>
              </ul>
              <Link to="/courses/Empresas" className="modal-link">Ver programas →</Link>
            </div>
          </div>

        </div>
      </section>

      {/* ── GALLERY ── */}
      <section className="gallery-section">
        <div className="container">
          <h2 className="section-title">Momentos Culturlingua</h2>
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 4000 }}
            loop={true}
            spaceBetween={20}
            slidesPerView={1}
            breakpoints={{ 768: { slidesPerView: 2 }, 1024: { slidesPerView: 3 } }}
            className="modern-swiper"
          >
            {[
              { src: './assets/virtual-class.jpeg', caption: 'Clases en Vivo' },
              { src: './assets/school.avif', caption: 'Comunidad Real' },
              { src: './assets/tlaquepaque.jpg', caption: 'Cultura y Lengua' },
              { src: './assets/number.webp', caption: 'Resultados Medibles' },
            ].map(({ src, caption }) => (
              <SwiperSlide key={caption}>
                <div className="gallery-card">
                  <img src={src} alt={caption} />
                  <div className="caption">{caption}</div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="testimonials-section">
        <div className="testimonials-header">
          <div className="section-tag">Testimonios</div>
          <h2 className="section-headline" style={{color:'white'}}>Lo que dicen nuestros <em style={{color:'var(--gold-light)'}}>alumnos</em></h2>
          <p className="section-sub" style={{color:'rgba(255,255,255,0.5)', margin:'0 auto'}}>Resultados reales de personas reales.</p>
        </div>
        <div className="testimonials-grid">

          <div className="testi-card">
            <div className="testi-stars">★★★★★</div>
            <p className="testi-text">
              En Culturlingua la manera de trabajar es muy organizada y dinámica los maestros se enfocan en que realmente practiquemos el idioma y explican con claridad.
            </p>
            <div className="testi-author">
              <div className="testi-avatar">NR</div>
              <div>
                <div className="testi-name">Nicole R.</div>
                <div className="testi-detail">Nivel A2 · Modalidad Online</div>
              </div>
            </div>
          </div>

          <div className="testi-card featured">
            <div className="testi-stars">★★★★★</div>
            <p className="testi-text">
              Una buena escuela, mi niña y yo estamos aprendiendo inglés, los maestros son muy dedicados y cada término de nivel realizan un proyecto donde exponen los conocimientos aprendidos, son muy dinámicas las clases y los costos muy accesibles a comparación con otras escuelas, yo sí la recomiendo
            </p>
            <div className="testi-author">
              <div className="testi-avatar">YV</div>
              <div>
                <div className="testi-name">Yaneth V.</div>
                <div className="testi-detail">Nivel B1 · Modalidad Presencial</div>
              </div>
            </div>
          </div>

          <div className="testi-card">
            <div className="testi-stars">★★★★★</div>
            <p className="testi-text">
              Una muy buena escuela de inglés, los maestros son muy buenos y pacientes, tienen un muy buen nivel de inglés.
Hacen sus clases muy dinámicas con actividades como pijamadas, juegos etc.
De lo que más me gusta es que cada que termina el nivel hacen una clase pública donde los papás o familiares pueden ver el avance que los niños o jóvenes han tenido.
El ambiente es muy ameno juvenil y familiar.
            </p>
            <div className="testi-author">
              <div className="testi-avatar">LA</div>
              <div>
                <div className="testi-name">Lizeth A.</div>
                <div className="testi-detail">Madre de alumn@s · Modalidad Presencial</div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── PRÓXIMOS INICIOS ── */}
      <section className="dates-section">
        <div className="dates-inner">

          <div>
            <div className="section-tag">Inscripciones Abiertas</div>
            <h2 className="section-headline">Próximos<br /><em>Inicios</em></h2>
            <p className="section-sub">Reserva tu lugar antes de que se llenen los grupos.</p>
            <div className="proximos-dates">
              {data.length > 0 ? data.map((item, index) => (
                <Link to="/contacto" className={`date-row${index === 0 ? ' next' : ''}`} key={item.id}>
                  <div className={`date-badge${index === 0 ? ' gold-bg' : ''}`}>
                    <span>{index === 0 ? '🚀' : '📅'}</span>
                  </div>
                  <div className="date-info">
                    <div className="date-label">{item.fecha}</div>
                    <div className="date-meta">Presencial y Online · Todos los niveles</div>
                  </div>
                  {index === 0
                    ? <span className="date-next-badge">Próximo</span>
                    : <span className="date-arrow">→</span>
                  }
                </Link>
              )) : (
                <div className="date-row" style={{justifyContent:'center', color:'var(--muted)'}}>
                  Cargando fechas...
                </div>
              )}
            </div>
          </div>

          <div className="exam-big-card">
            <div className="section-tag">Gratis · 10 minutos</div>
            <h3>¿No sabes qué nivel eres?</h3>
            <p>Haz nuestro examen de ubicación y te diremos exactamente en qué nivel empezar.</p>
            <div className="exam-pills">
              <span className="exam-pill">⏱️ 10 min</span>
              <span className="exam-pill">🆓 Gratis</span>
              <span className="exam-pill">📱 Cualquier dispositivo</span>
              <span className="exam-pill">🏆 Resultado inmediato</span>
            </div>
            <Link to="/Examen-de-ubicacion" className="exam-btn">
              Hacer el examen gratis →
            </Link>
            <p className="exam-fine">Sin registro. Sin tarjeta. Sin compromisos.</p>
          </div>

        </div>
      </section>

      {/* ── CONTACT ── */}
      <section className="contact-section">
        <div className="contact-inner">

          <div className="contact-left">
            <div className="section-tag">Contáctanos</div>
            <h2 className="section-headline">Hablemos de<br /><em>tus metas</em></h2>
            <p className="section-sub">¿Dudas entre Online o Presencial? ¿No sabes tu nivel? Te ayudamos sin compromiso.</p>
            <div className="contact-info-items">
              <div className="contact-info-item">
                <div className="contact-info-icon">📍</div>
                <div className="contact-info-text">
                  <strong>Dirección</strong>
                  <span>Reforma 31, Tlaquepaque, Jalisco, México</span>
                </div>
              </div>
              <div className="contact-info-item">
                <div className="contact-info-icon">📞</div>
                <div className="contact-info-text">
                  <strong>Teléfono</strong>
                  <span>+52 33 3344 9139</span>
                </div>
              </div>
              <div className="contact-info-item">
                <div className="contact-info-icon">💬</div>
                <div className="contact-info-text">
                  <strong>WhatsApp</strong>
                  <span>Respuesta en menos de 1 hora en horario de oficina</span>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-right">
            {mailSubmitted ? (
              <div style={{textAlign:'center', padding:'40px 0'}}>
                <div style={{fontSize:'3rem', marginBottom:'16px'}}>✅</div>
                <h3 style={{fontFamily:'Fraunces, serif', fontSize:'1.6rem', marginBottom:'10px'}}>¡Mensaje enviado!</h3>
                <p style={{color:'var(--muted)'}}>Te contactamos en menos de 1 hora en horario de oficina.</p>
              </div>
            ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-row" style={{marginBottom:'16px'}}>
                <div className="form-field" style={{marginBottom:0}}>
                  <label className="form-label">Nombre</label>
                  <input className="form-input" type="text" name="nombre" placeholder="Tu nombre" required />
                </div>
                <div className="form-field" style={{marginBottom:0}}>
                  <label className="form-label">WhatsApp</label>
                  <input className="form-input" type="tel" name="telefono" placeholder="+52 33..." required />
                </div>
              </div>
              <div className="form-field">
                <label className="form-label">Correo electrónico</label>
                <input className="form-input" type="email" name="correo" placeholder="tu@correo.com" required />
              </div>
              <div className="form-field">
                <label className="form-label">¿En qué podemos ayudarte?</label>
                <textarea className="form-input form-textarea" name="pregunta" placeholder="Cuéntanos tu situación..." required></textarea>
              </div>
              {ErrorBox}
              <button type="submit" className="form-submit" disabled={mailLoading}>
                {mailLoading ? 'Enviando...' : 'Enviar Mensaje →'}
              </button>
            </form>
            )}
          </div>

        </div>
      </section>

    </div>
  );
}

export default Home;
